import { motion } from "framer-motion";
import { Star, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/translations";
import { resolveImg } from "@/lib/api";
import { Reveal } from "./Reveal";

const scrollToMenu = () => document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" });

export const Suggested = ({ items = [] }) => {
  const { lang } = useLang();
  const picks = items.filter((i) => i.suggested && i.image);
  if (picks.length === 0) return null;

  return (
    <section id="suggested" className="relative bg-[#0A0A0A] py-20 md:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <Reveal>
              <span className="font-accent text-2xl text-[#A8E010]">{t("sug_kicker", lang)}</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] mt-2">{t("sug_title", lang)}</h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <button onClick={scrollToMenu} data-testid="sug-see-menu"
              className="group inline-flex items-center gap-2 text-sm uppercase tracking-widest text-neutral-300 hover:text-[#FF5A00] transition-colors">
              {t("sug_all", lang)} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </Reveal>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6 lg:gap-8">
          {picks.map((it, i) => (
            <motion.article
              key={it.id}
              data-testid={`sug-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              onClick={scrollToMenu}
              className="group relative h-[420px] md:h-[500px] overflow-hidden rounded-3xl border border-white/10 cursor-pointer"
            >
              <img src={resolveImg(it.image)} alt={lang === "es" ? it.name_es : it.name_en}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full bg-[#FF5A00] text-black text-xs font-bold uppercase tracking-wide px-3 py-1.5">
                <Star size={13} fill="currentColor" /> {t("sug_badge", lang)}
              </span>
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <h3 className="font-display uppercase text-4xl md:text-5xl leading-[0.9]">{lang === "es" ? it.name_es : it.name_en}</h3>
                <p className="text-sm text-neutral-300 mt-2 max-w-md">{lang === "es" ? it.desc_es : it.desc_en}</p>
                <div className="mt-4 flex items-center gap-4">
                  <span className="font-display text-3xl text-[#A8E010]">{it.price}</span>
                  <span className="text-xs uppercase tracking-widest text-neutral-400 group-hover:text-white transition-colors">{t("sug_order", lang)}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
